import React, { useEffect, useState, useRef } from "react";
import { AxiosError } from "axios";
import { Result, CustomGroup } from "../../interface/CustomIcd";
import { CustomIcdAxiosInstance } from "../../Network/CustomIcd";
import { Option } from "../../interface/Option";
import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../../components/ui/button";
import ListofMedicalGroup from "../Shared/ListofMedicalGroup";
import ListofCustom from "Pages/Shared/ListofcustemIcd";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import Listofmedicalcode from "../Shared/ListofIcdmedicalcode";
import { Eye } from "lucide-react";
import { X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import DataTable2 from "shared/DataTable2";
import UnifiedSearchBox from "../../shared/UnifiedSearchBox";
import ServerDeletedPills from "shared/ServerDeletedPills";
import { ServerDeletedFilterValue } from "shared/ServerListFilters";
import { useServerPaginatedList } from "shared/useServerPaginatedList";
import FileUpload from "../uploadFile/UploadFileCustomGroup/UploadFileCustomGroup";

// ─── Role Helper ──────────────────────────────────────────────────────────────
const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch { return ""; }
};

// ─── Status Dropdown ──────────────────────────────────────────────────────────
const StatusDropdown: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const options = [
    { value: "",      label: "All Status" },
    { value: "true",  label: "Active"     },
    { value: "false", label: "Deactivate" },
  ];
  const selected = options.find((o) => o.value === value) || options[0];

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative", userSelect: "none", minWidth: 150 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", height: 38, padding: "0 12px",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          background: "#fff", border: `1.5px solid ${open ? "#1e3a8a" : "#cbd5e1"}`,
          borderRadius: 8, fontSize: 14, color: "#1e293b",
          cursor: "pointer", transition: "border-color 0.15s", boxSizing: "border-box" as const,
        }}
      >
        <span style={{ color: value ? "#1e293b" : "#94a3b8", fontWeight: value ? 500 : 400 }}>
          {selected.label}
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke={open ? "#1e3a8a" : "#94a3b8"} strokeWidth="2.5" strokeLinecap="round"
          style={{ flexShrink: 0, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, width: "100%",
          background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.09)", zIndex: 9999, overflow: "hidden",
        }}>
          {options.map((opt) => {
            const isSel = opt.value === value;
            return (
              <div key={opt.value}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                style={{
                  padding: "10px 12px", fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: isSel ? "#eff6ff" : "transparent",
                  color: isSel ? "#1e3a8a" : "#374151",
                  fontWeight: isSel ? 600 : 400,
                  borderBottom: "1px solid #f1f5f9",
                }}
                onMouseEnter={(e) => { if (!isSel) (e.currentTarget as HTMLDivElement).style.background = "#f8fafc"; }}
                onMouseLeave={(e) => { if (!isSel) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <span>{opt.label}</span>
                {isSel && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="#1e3a8a" strokeWidth="2.5" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
// ─── SectionCard ──────────────────────────────────────────────────────────────
const SectionCard: React.FC<{ num: number; title: string; subtitle?: string; children: React.ReactNode }> = ({ num, title, subtitle, children }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 bg-white shadow-sm">
    <div className="flex items-center gap-3 px-5 py-3 bg-gray-50 border-b border-gray-200">
      <span className="w-6 h-6 rounded-full bg-blue-700 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">{num}</span>
      <div>
        <p className="text-sm font-semibold text-gray-800">{title}</p>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── StatusToggle ─────────────────────────────────────────────────────────────
const StatusToggle: React.FC<{ label: string; dotColor: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, dotColor, value, onChange }) => (
  <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dotColor }} />
      <span className="text-xs font-medium text-gray-600">{label}</span>
    </div>
    <div className="flex gap-2">
      <button type="button" onClick={() => onChange(true)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${value ? "bg-green-50 text-green-800 border-green-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Active
      </button>
      <button type="button" onClick={() => onChange(false)}
        className={`flex-1 py-1.5 rounded-md text-xs font-medium border transition-all ${!value ? "bg-red-50 text-red-800 border-red-300" : "bg-white text-gray-400 border-gray-200 hover:bg-gray-100"}`}>
        Inactive
      </button>
    </div>
  </div>
);

// ─── ICD Search Input ─────────────────────────────────────────────────────────
const IcdSearchInput: React.FC<{ value: string; onChange: (v: string) => void; placeholder?: string }> = ({ value, onChange, placeholder = "Search ICD code..." }) => {
  return (
    <div style={{ position: "relative", minWidth: 200 }}>
      <div style={{
        position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none", display: "flex", alignItems: "center",
      }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          height: 38, width: "100%", padding: "0 30px 0 30px",
          border: `1.5px solid ${value ? "#1e3a8a" : "#cbd5e1"}`,
          borderRadius: 8, fontSize: 14, color: "#1e293b",
          outline: "none", transition: "border-color 0.15s", boxSizing: "border-box" as const,
          background: value ? "#eff6ff" : "#fff",
        }}
      />
      {value && (
        <button
          onClick={() => onChange("")}
          style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "#e2e8f0", border: "none", borderRadius: "50%",
            width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", fontSize: 11, color: "#64748b", padding: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CustomIcdGroups: React.FC = () => {
  const [items, setItems] = useState<CustomGroup>({ result: [] });

  // ── Role-based access ────────────────────────────────────────────────────
  const userRole = getUserRole();
  const canManageDeleted = userRole === "system_admin" || userRole === "super_super_admin";

  // ── Force Dialog state ───────────────────────────────────────────────────
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
const [forceAction, setForceAction] = useState<"softDelete" | "hardDelete" | "status" | null>(null);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const [Name, setName] = useState<string>("");
  const [IcdDescription, setIcdDescription] = useState<string>("");
  const [medicalcode, setMedicalcode] = useState<Option[]>([]);
  const [selectedIcds, setSelectedIcds] = useState<string[]>([]);
  const icdDialogRef = useRef<HTMLDialogElement | null>(null);
  const [Description, setDescription] = useState<string>("");
  const [medicalIcdGroups, setMedicalIcdGroups] = useState<Option[]>([]);
  const [CustomIcdGroups, setCustomIcdGroups] = useState<Option[]>([]);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const [clearmedicalIcdGroups, setClearMedicalIcdGroups] = useState(false);
  const [clearcustomIcdGroups, setClearCustomIcdGroups] = useState(false);
  const [clearmedicalcode, setClearMedicalcode] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Result | null>(null);
  const dialogRef = useRef<HTMLDialogElement | null>(null);
  const token = localStorage.getItem("token") || "";
  const { toast } = useToast();
  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [allCodesOpen, setAllCodesOpen] = useState(false);
  const [allCodesItems, setAllCodesItems] = useState<Array<{ label: string; type: string }>>([]);
  const [allCodesTitle, setAllCodesTitle] = useState("");
  const [forceTargetItem, setForceTargetItem] = useState<any>(null);
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  // ─── Filter States ────────────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [deletedFilter, setDeletedFilter] = useState<ServerDeletedFilterValue>("false");
  const [filterSearch, setFilterSearch] = useState<string>("");

  const labelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 500, color: "#9ca3af",
    textTransform: "capitalize", letterSpacing: "0.6px", marginBottom: 6,
  };
  const valStyle: React.CSSProperties = {
    margin: 0, fontSize: 14, fontWeight: 500, color: "#111827",
  };
  const dividerStyle: React.CSSProperties = {
    height: "0.5px", background: "#e5e7eb",
  };

  // ─── Server-side pagination and filters ───────────────────────────────────
  // The same unified search value is sent as both `name` and `disease`:
  // - `name` searches the Custom ICD Group name.
  // - `disease` searches ICD codes and the linked Medical/Custom Group names.
  const listFilters = {
    search: filterSearch.trim(),
    is_active: filterStatus,
    is_deleted: deletedFilter,
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<Result, typeof listFilters>({
    filters: listFilters,
    fetchPage: ({ page, limit, filters }) => {
      const search = filters.search || undefined;

      return CustomIcdAxiosInstance.get("getCustomGroup", {
        params: {
          page,
          limit,
          name: search,
          disease: search,
          is_active: filters.is_active || undefined,
          is_deleted: filters.is_deleted,
        },
      });
    },
    setRows: (rows) => setItems({ result: rows }),
    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id || 0) - Number(a.id || 0))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted ? 0 : a.is_deleted ? 1 : -1,
        ),
    onError: (error) => {
      console.error("Error fetching Custom ICD Groups:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Custom ICD Groups",
        variant: "destructive",
      });
    },
  });

  const filteredData = items.result;

  useEffect(() => { fetchUsers(); }, []);

  // ── Soft Delete ──────────────────────────────────────────────────────────
  const removeItem = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.delete(`softDeleteCustomGroup/${id}`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Custom ICD group Deleted Successfully." });
      } else {
        setForceId(id); setForceAction("softDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg = error.response?.data?.error || "Failed to delete";
      if (error.response?.status === 409 || errMsg.toLowerCase().includes("used")) {
        setForceId(id); setForceAction("softDelete"); setForceError(errMsg); setForceDialogOpen(true);
      } else {
        toast({ title: "Error", description: errMsg, variant: "destructive" });
      }
    }
  };

  // ── Restore ──────────────────────────────────────────────────────────────
  const restoreItem = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.patch(`restoreCustomGroup/${id}`, { token });
      if (response.data.success) {
        await fetchItems();
        toast({ title: "Success", description: "Custom ICD group restored successfully." });
      } else {
        toast({ title: "Error", description: response.data.error || "Failed to restore", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.response?.data?.error || "Failed to restore", variant: "destructive" });
    }
  };

  // ── Hard Delete ──────────────────────────────────────────────────────────
  const permanentDeleteItem = async (id: string | number, force = false): Promise<void> => {
    try {
      const url = force ? `deleteCustomGroup/${id}?force=true` : `deleteCustomGroup/${id}`;
      const response = await CustomIcdAxiosInstance.delete(url, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Custom ICD group Permanently Deleted." });
      } else {
        setForceAction("hardDelete");
        setForceError(response.data.error || "This record is used elsewhere.");
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(error.response?.data?.error || "Failed to delete");
    }
  };

  // ── Force Soft Delete ────────────────────────────────────────────────────
  const forceSoftDelete = async (id: string | number): Promise<void> => {
    try {
      const response = await CustomIcdAxiosInstance.delete(`softDeleteCustomGroup/${id}?force=true`, { data: { token } });
      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null);
        toast({ title: "Success", description: "Custom ICD group force Deleted Successfully." });
      } else {
        setForceError(response.data.error || "Failed to force delete");
      }
    } catch (error: any) {
      setForceError(error.response?.data?.error || "Failed to force delete");
    }
  };

  const handleOpenDialog  = (item: Result) => { setSelectedItem(item); dialogRef.current?.showModal(); };
const handleCloseDialog = () => { setSelectedItem(null); setShowAllUpdates(false); };
  const handleOpenIcdDialog  = (icds: string[]) => { setSelectedIcds(icds); icdDialogRef.current?.showModal(); };
  const handleCloseIcdDialog = () => { icdDialogRef.current?.close(); setSelectedIcds([]); };
  const handleEditClick = (item: Result) => { handleEdit(item); setIsDialogOpen(true); };
  const handleAddClick  = () => { setEditIndex(null); clear(); setIsDialogOpen(true); };

  const handlemedicalicdChange  = (selectedOptions: Option[]) => setMedicalIcdGroups(selectedOptions);
  const handlecustomicdChange   = (selectedOptions: Option[]) => setCustomIcdGroups(selectedOptions);
  const handlemedicalcodeChange = (selectedOptions: Option[]) => setMedicalcode(selectedOptions);

  const clear = () => {
    setName(""); setMedicalcode([]); setDescription("");
    setMedicalIcdGroups([]); setCustomIcdGroups([]);
    setClearMedicalIcdGroups(true); setClearCustomIcdGroups(true); setIsActive(true);
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      const map: Record<number, string> = {};
      raw.forEach((u: any) => {
        if (u.id != null) map[Number(u.id)] = u.name ?? u.username ?? String(u.id);
      });
      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object") return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

const toggleActiveStatus = async (item: Result, force = false): Promise<void> => {
  try {
    const ValuesMedical = (item.medical_groups || []).map((g: any) => g.id ?? g);
    const ValuesCustom  = (item.custom_groups  || []).map((g: any) => g.id ?? g);
    const url = force ? "updateCustomGroup?force=true" : "updateCustomGroup";
    const response = await CustomIcdAxiosInstance.put(url, {
      data: { name: item.name, icd: item.icd || [], description: item.description,
        medical_groups: ValuesMedical, custom_groups: ValuesCustom,
        is_active: !item.is_active, id: item.id },
      token,
    });
    if (response.data.success) {
      setItems((prev) => ({
        result: prev.result.map((r) => r.id === item.id ? { ...r, is_active: !item.is_active } : r),
      }));
      setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null);
      toast({ title: "Success", description: `Status changed to ${!item.is_active ? "Active" : "Inactive"}.` });
    } else {
      setForceId(item.id);
      setForceTargetItem(item);
      setForceAction("status");
      setForceError(response.data.error || "This record is used elsewhere.");
      setForceDialogOpen(true);
    }
  } catch (error: any) {
    const errMsg = error.response?.data?.error || "";
    if (error.response?.status === 409 || errMsg.toLowerCase().includes("used") || errMsg.toLowerCase().includes("referenced") || errMsg.toLowerCase().includes("cannot")) {
      setForceId(item.id); setForceTargetItem(item); setForceAction("status");
      setForceError(errMsg || "This record is used elsewhere."); setForceDialogOpen(true);
    } else {
      toast({ title: "Error", description: errMsg || "An unexpected error occurred.", variant: "destructive" });
    }
  }
};

  const addItem = async (): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one ICD code is required.", variant: "destructive" }); return; }
    try {
      const payload = {
        data: [{
          name: Name, icd: medicalcode.map((o) => o.value), description: Description,
          medical_groups: medicalIcdGroups.map((o) => o.value),
          custom_groups: CustomIcdGroups.map((o) => o.value), is_active: isActive,
        }],
        token,
      };
      const response = await CustomIcdAxiosInstance.post("addNewCustomGroup", payload);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "New Custom ICD Group Added Successfully." });
      } else {
        const err = response.data.errors?.[0];
        toast({ title: "Error", description: err?.error || response.data.error || "Failed to add the group.", variant: "destructive" });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleEdit = (item: Result) => {
    setName(item.name);
    setMedicalcode(item.icd.map((code) => ({ value: code, label: code })));
    const MedicalIcdGroupsData = Array.isArray(item.medical_groups)
      ? item.medical_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setMedicalIcdGroups(MedicalIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));
    const CustomIcdGroupsData = Array.isArray(item.custom_groups)
      ? item.custom_groups.map((obj) => typeof obj === "string" ? { id: 0, name: obj } : obj) : [];
    setCustomIcdGroups(CustomIcdGroupsData.map((obj) => ({ value: obj.id, label: obj.name })));
    setDescription(item.description);
    setIsActive(item.is_active);
    setEditIndex(item.id);
  };

  const updateItem = async (id: string | number): Promise<void> => {
    if (!Name.trim()) { toast({ title: "Error", description: "Name is required and cannot be empty.", variant: "destructive" }); return; }
    if (Name.trim().length < 3) { toast({ title: "Error", description: "Name must be at least 3 characters long.", variant: "destructive" }); return; }
    if (!medicalcode.length || medicalcode.every((option) => !option.value)) { toast({ title: "Error", description: "At least one ICD code is required.", variant: "destructive" }); return; }
    try {
      const updatedItem = {
        data: {
          name: Name, icd: medicalcode.map((o) => o.value), description: Description,
          medical_groups: medicalIcdGroups.map((o) => o.value),
          custom_groups: CustomIcdGroups.map((o) => o.value), is_active: isActive, id,
        },
        token,
      };
      const response = await CustomIcdAxiosInstance.put(`updateCustomGroup`, updatedItem);
      if (response.data.success) {
        await fetchItems();
        setCurrentPage(0);
        clear(); setIsDialogOpen(false);
        toast({ title: "Success", description: "Custom ICD Group updated successfully." });
      }
    } catch (error: AxiosError | any) {
      toast({ title: "Error", description: error.response?.data?.error || "An unexpected error occurred.", variant: "destructive" });
    }
  };

  const handleDownload = (item: Result) => {
    const rows = ["name;description;icd;medicalGroups;customGroups;is_active"];
    const icdCodes     = item.icd || [];
    const medicalGroups = item.medical_groups || [];
    const customGroups  = item.custom_groups  || [];
    const maxLen = Math.max(icdCodes.length, medicalGroups.length, customGroups.length);
    if (maxLen > 0) {
      rows.push(`${item.name || ""};${item.description || ""};${icdCodes[0] || ""};${medicalGroups[0]?.name || ""};${customGroups[0]?.name || ""};${item.is_active ? "TRUE" : "FALSE"}`);
      for (let i = 1; i < maxLen; i++) {
        rows.push(`${item.name || ""};;${icdCodes[i] || ""};${medicalGroups[i]?.name || ""};${customGroups[i]?.name || ""};`);
      }
    } else {
      rows.push(`${item.name || ""};${item.description || ""};;;;${item.is_active ? "TRUE" : "FALSE"}`);
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `custom_icd_group_${item.id}.csv`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const columns: ColumnDef<Result>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ID{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
     cell: ({ row }) => (
  <div className="w-full flex justify-center font-medium">{row.index + 1}</div>
),
    },
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Name{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <div className={`w-full text-center ${(row.original as any).is_deleted ? "line-through text-gray-400" : ""}`}>
          {row.getValue("name")}
        </div>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          Description{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => <div className="w-full text-center">{row.getValue("description")}</div>,
    },
    {
      accessorKey: "icd",
      header: ({ column }) => (
        <Button variant="ghost" className="w-full justify-center"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}>
          ICD{column.getIsSorted() === "asc" ? " ↑" : column.getIsSorted() === "desc" ? " ↓" : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const icds = Array.isArray(item.icd) ? item.icd : [];
        const customGroups = Array.isArray(item.custom_groups)
          ? item.custom_groups.map((g: any) => ({ label: g.name || g, type: "Custom" })) : [];
        const medicalGroups = Array.isArray(item.medical_groups)
          ? item.medical_groups.map((g: any) => ({ label: g.name || g, type: "Medical" })) : [];
        const allValues = [
          ...icds.map((c: string) => ({ label: c, type: "ICD" })),
          ...medicalGroups,
          ...customGroups,
        ];
        if (allValues.length === 0) return <div className="w-full text-center text-gray-400">-</div>;
        const searchTerm = filterSearch.toLowerCase().trim();
        const hasSearch = searchTerm.length >= 2;
        const matchingValues = hasSearch ? allValues.filter((c) => c.label.toLowerCase().includes(searchTerm)) : [];
        const displayedValues = hasSearch && matchingValues.length > 0 ? matchingValues.slice(0, 3) : allValues.slice(0, 3);
        const remainingCount = hasSearch && matchingValues.length > 0 ? matchingValues.length - 3 : allValues.length - 3;
        const getStyle = (type: string, isMatching: boolean) => {
          if (isMatching) return { bg: "#fef08a", color: "#713f12", border: "#facc15", tag: type === "ICD" ? "ICD" : type === "Medical" ? "MED" : "CUS" };
          if (type === "ICD") return { bg: "#011934", color: "#fff", border: "#011934", tag: "ICD" };
          if (type === "Medical") return { bg: "#f0fdf4", color: "#15803d", border: "#86efac", tag: "MED" };
          return { bg: "#faf5ff", color: "#7e22ce", border: "#d8b4fe", tag: "CUS" };
        };
        return (
          <div className="w-full px-2 py-1.5">
            <div className="flex flex-wrap justify-center gap-1.5 mb-1">
              {displayedValues.map((c, idx) => {
                const isMatching = hasSearch && c.label.toLowerCase().includes(searchTerm);
                const style = getStyle(c.type, isMatching);
                return (
                  <div key={idx} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 4px 2px 8px", borderRadius: 6, background: style.bg, border: `1px solid ${style.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: style.color }}>{c.label}</span>
                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "rgba(255,255,255,0.2)", color: style.color, opacity: 0.85, border: `1px solid ${style.border}` }}>{style.tag}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between border-t border-gray-100 pt-1">
              <span className="text-xs font-bold text-gray-500">{hasSearch && matchingValues.length > 0 ? `${matchingValues.length}/${allValues.length}` : allValues.length}</span>
              {remainingCount > 0 && (
                <button onClick={() => { setAllCodesItems(hasSearch && matchingValues.length > 0 ? matchingValues : allValues); setAllCodesTitle(`${item.name} – ICD Codes`); setAllCodesOpen(true); }}
                  className={`text-xs font-semibold ${hasSearch ? "text-yellow-600 hover:text-yellow-800" : "text-blue-600 hover:text-blue-800"}`}>
                  +{remainingCount} more →
                </button>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "is_active",
      enableSorting: false,
      header: () => <div className="w-full text-center normal-case font-normal">Status</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDeleted = (item as any).is_deleted;
        return (
          <div className="w-full flex justify-center">
            <button
              onClick={() => !isDeleted && toggleActiveStatus(item)}
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isDeleted ? "opacity-40 cursor-not-allowed bg-gray-200" : item.is_active ? "bg-green-500" : "bg-gray-300"}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${item.is_active && !isDeleted ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      enableSorting: false,
      header: () => <div className="w-full text-center font-normal">View</div>,
      cell: ({ row }) => (
        <div className="w-full flex justify-center">
          <Button size="sm" variant="ghost" className="p-2 hover:bg-blue-50 text-blue-600 transition"
            onClick={() => handleOpenDialog(row.original)} title="View Details">
            <Eye className="text-lg" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-file-medical-alt"></i>
          </span>
          Custom ICD Groups
        </h5>
      </div>

      <DataTable2
        columns={columns}
        data={filteredData}
        searchColumns={[]}
        addButtonLabel="Add New Group"
        showAddButton={false}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(i => i.id === id);
          if ((item as any)?.is_deleted) {
            setForceId(id); setForceAction("hardDelete"); setForceError(""); setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={canManageDeleted ? restoreItem : undefined}
canHardDelete={canManageDeleted}
        isRowDeleted={(row: any) => row.is_deleted === true}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        toolbarExtras={
          <div style={{ width: "100%", position: "relative" }}>
<div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 160px 10px 16px", background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)", borderRadius: 12, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)", marginBottom: 12, boxSizing: "border-box" as const }}>
  <ServerDeletedPills
    value={deletedFilter}
    onChange={(value) => { setDeletedFilter(value); setCurrentPage(0); }}
    />
              <button type="button" onClick={handleAddClick} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#ffffff", background: "#1e3a8a", border: "none", borderRadius: 8, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, boxShadow: "0 2px 6px rgba(30,58,138,0.25)" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
                Add New Group
              </button>
              <UnifiedSearchBox
                value={filterSearch}
                onChange={(value) => { setFilterSearch(value); setCurrentPage(0); }}
                helpText="Group Name, ICD, Medical Group, or Custom Group."
              />
              <div style={{ width: 1, height: 28, background: "#cbd5e1", flexShrink: 0 }} />
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em" }}>Status</span>
                <div style={{ display: "flex", alignItems: "center", padding: 3, background: "#e2e8f0", borderRadius: 9999 }}>
                  {[{ value: "", label: "All" }, { value: "true", label: "Active" }, { value: "false", label: "Inactive" }].map((option) => (
                    <button key={option.value} type="button" onClick={() => { setFilterStatus(option.value); setCurrentPage(0); }} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 11px", fontSize: 12, fontWeight: 500, borderRadius: 9999, border: "none", cursor: "pointer", transition: "all 0.2s ease", whiteSpace: "nowrap", background: filterStatus === option.value ? option.value === "true" ? "#10b981" : option.value === "false" ? "#94a3b8" : "#ffffff" : "transparent", color: filterStatus === option.value ? option.value === "" ? "#334155" : "#ffffff" : "#64748b", boxShadow: filterStatus === option.value ? "0 2px 4px rgba(0,0,0,0.1)" : "none" }}>
                      {option.value === "true" && filterStatus === "true" && (<span style={{ width: 5, height: 5, borderRadius: "50%", background: "#ffffff" }} />)}
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", top: 0, right: 16, height: 62, display: "flex", alignItems: "center" }}>
              <FileUpload fetchItems={fetchItems} />
            </div>
          </div>
        }
      />

      {/* ── ICD List Dialog ── */}
      <Dialog open={allCodesOpen} onOpenChange={setAllCodesOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <i className="fa-solid fa-file-medical text-blue-600" />
              {allCodesTitle}
              <span className="ml-2 text-sm font-normal text-gray-400">({allCodesItems.length} codes)</span>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-3 overflow-y-auto max-h-[calc(80vh-120px)] space-y-4">
            {allCodesItems.filter((x) => x.type === "ICD").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#011934", display: "inline-block" }} />
                  <span className="text-xs font-bold text-gray-600 tracking-wide">ICD Codes ({allCodesItems.filter((x) => x.type === "ICD").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "ICD").map((c, i) => (<span key={i} style={{ padding: "4px 12px", background: "#011934", color: "white", borderRadius: 6, fontSize: 12, fontWeight: 600 }}>{c.label}</span>))}
                </div>
              </div>
            )}
            {allCodesItems.filter((x) => x.type === "Medical").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", display: "inline-block" }} />
                  <span className="text-xs font-bold text-green-700 tracking-wide">Medical ICD Groups ({allCodesItems.filter((x) => x.type === "Medical").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "Medical").map((c, i) => (<span key={i} style={{ padding: "4px 12px", background: "#f0fdf4", color: "#15803d", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #86efac" }}>{c.label}</span>))}
                </div>
              </div>
            )}
            {allCodesItems.filter((x) => x.type === "Custom").length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: "#9333ea", display: "inline-block" }} />
                  <span className="text-xs font-bold text-purple-700 tracking-wide">Custom ICD Groups ({allCodesItems.filter((x) => x.type === "Custom").length})</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {allCodesItems.filter((x) => x.type === "Custom").map((c, i) => (<span key={i} style={{ padding: "4px 12px", background: "#faf5ff", color: "#7e22ce", borderRadius: 6, fontSize: 12, fontWeight: 600, border: "1px solid #d8b4fe" }}>{c.label}</span>))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-3 border-t mt-3">
            <Button onClick={() => setAllCodesOpen(false)} className="bg-blue-700 hover:bg-blue-800">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── View Details Dialog ── */}
      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => { if (!open) handleCloseDialog(); }}>
        <DialogContent className="max-w-[780px] max-h-[92vh] flex flex-col p-0 overflow-hidden bg-white border-none rounded-2xl" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)" }}>
          {selectedItem && (
            <>
              <div style={{ background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)", padding: "24px 28px 20px", flexShrink: 0, position: "relative", overflow: "hidden" }}>
                <div style={{ position: "absolute", top: -40, right: -40, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
                <div style={{ position: "absolute", bottom: -30, left: -20, width: 100, height: 100, borderRadius: "50%", background: "rgba(255,255,255,0.03)" }} />
                <div style={{ display: "flex", alignItems: "flex-start", gap: 16, position: "relative", zIndex: 1 }}>
                  <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, color: "#fff", flexShrink: 0, border: "1px solid rgba(255,255,255,0.1)" }}>
                    <i className="fa-solid fa-file-medical-alt" />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 2 }}>
                      <h2 style={{ fontSize: 20, fontWeight: 700, color: "#fff", margin: 0, letterSpacing: "-0.01em", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedItem.name}</h2>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: selectedItem.is_active ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.15)", color: selectedItem.is_active ? "#86efac" : "#fca5a5", border: `1px solid ${selectedItem.is_active ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.2)"}` }}>
                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />
                        {selectedItem.is_active ? "Active" : "Inactive"}
                      </span>
                      {(selectedItem as any).is_deleted && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 12px", borderRadius: 9999, fontSize: 11, fontWeight: 600, flexShrink: 0, background: "rgba(239,68,68,0.15)", color: "#fca5a5", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <i className="fa-solid fa-trash" style={{ fontSize: 10 }} /> Deleted
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "rgba(147,197,253,0.8)", margin: 0, fontWeight: 400 }}>Group ID: <span style={{ fontWeight: 600, color: "#93c5fd" }}>#{selectedItem.id}</span></p>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 18, position: "relative", zIndex: 1 }}>
                  {[
                    { icon: "fa-file-medical", label: "ICD Codes", value: `${(selectedItem.icd || []).length} codes`, accent: "#60a5fa" },
                    { icon: "fa-layer-group", label: "Medical Groups", value: `${(selectedItem.medical_groups || []).length}`, accent: "#a78bfa" },
                    { icon: "fa-cubes", label: "Custom Groups", value: `${(selectedItem.custom_groups || []).length}`, accent: "#34d399" },
                    { icon: "fa-align-left", label: "Description", value: selectedItem.description ? "Available" : "None", accent: "#f59e0b" },
                  ].map((card, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.07)", backdropFilter: "blur(6px)", borderRadius: 12, padding: "12px 14px", border: "1px solid rgba(255,255,255,0.08)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                        <i className={`fa-solid ${card.icon}`} style={{ fontSize: 11, color: card.accent, opacity: 0.9 }} />
                        <span style={{ fontSize: 10, color: "rgba(147,197,253,0.7)", fontWeight: 500, letterSpacing: "0.03em" }}>{card.label}</span>
                      </div>
                      <span style={{ fontSize: 15, fontWeight: 700, color: card.accent }}>{card.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ overflowY: "auto", flex: 1, background: "#f8fafc", padding: "6px 0 16px" }}>
                {selectedItem.description && (
                  <>
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #fde68a" }}>
                          <i className="fa-solid fa-align-left" style={{ fontSize: 12, color: "#d97706" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Description</span>
                      </div>
                      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                        <p style={{ margin: 0, fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{selectedItem.description}</p>
                      </div>
                    </div>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                  </>
                )}

                <div style={{ padding: "20px 28px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#eff6ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #dbeafe" }}>
                      <i className="fa-solid fa-file-medical" style={{ fontSize: 12, color: "#3b82f6" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>ICD Codes</span>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500 }}>{(selectedItem.icd || []).length} items</span>
                  </div>
                  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 6 }}>
                      {(selectedItem.icd || []).length > 0
                        ? (selectedItem.icd || []).map((code: string, i: number) => (<span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, background: "#eff6ff", color: "#1d4ed8", border: "0.5px solid #bfdbfe" }}>{code}</span>))
                        : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>No ICD codes</span>}
                    </div>
                  </div>
                </div>

                {((selectedItem.medical_groups || []).length > 0 || (selectedItem.custom_groups || []).length > 0) && (
                  <>
                    <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />
                    <div style={{ padding: "20px 28px 16px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 8, background: "#faf5ff", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e9d5ff" }}>
                          <i className="fa-solid fa-layer-group" style={{ fontSize: 12, color: "#a855f7" }} />
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Groups</span>
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        {[
                          { label: "Medical ICD Groups", color: "#22c55e", items: (selectedItem.medical_groups || []).map((g: any) => g.name || g), tagStyle: { background: "#f0fdf4", color: "#15803d", border: "0.5px solid #bbf7d0" } },
                          { label: "Custom ICD Groups", color: "#a855f7", items: (selectedItem.custom_groups || []).map((g: any) => g.name || g), tagStyle: { background: "#faf5ff", color: "#7e22ce", border: "0.5px solid #e9d5ff" } },
                        ].map((section) => (
                          <div key={section.label} style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
                              <span style={{ width: 6, height: 6, borderRadius: 2, background: section.color, flexShrink: 0 }} />
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", letterSpacing: "0.02em" }}>{section.label}</span>
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 5 }}>
                              {section.items.length > 0
                                ? section.items.map((item: string, i: number) => (<span key={i} style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 9999, fontSize: 11, fontWeight: 500, ...section.tagStyle }}>{item}</span>))
                                : <span style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>None</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                <div style={{ height: 1, background: "#e2e8f0", margin: "0 28px" }} />

                <div style={{ padding: "20px 28px 8px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #bbf7d0" }}>
                      <i className="fa-solid fa-clock-rotate-left" style={{ fontSize: 12, color: "#16a34a" }} />
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Audit Trail</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created By</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                          {String((selectedItem as any).created_by ?? "?")[0].toUpperCase()}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{resolveUserName((selectedItem as any).created_by)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Created On</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-calendar" style={{ fontSize: 14, color: "#3b82f6" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).createdat)}</span>
                      </div>
                    </div>
                    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", letterSpacing: "0.02em" }}>Last Updated</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
                        <i className="fa-regular fa-clock" style={{ fontSize: 14, color: "#10b981" }} />
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{formatDate((selectedItem as any).updatedat)}</span>
                      </div>
                    </div>
                  </div>
          {Array.isArray((selectedItem as any).updated_by) && (selectedItem as any).updated_by.length > 0 && (
  <div style={{ gridColumn: "1 / -1", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0", padding: "14px 16px", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <i className="fa-solid fa-pen-to-square" style={{ fontSize: 11, color: "#10b981" }} />
      <span style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.02em" }}>Update History</span>
      <span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#f0fdf4", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0", marginLeft: "auto" }}>
        {(selectedItem as any).updated_by.length} update{(selectedItem as any).updated_by.length !== 1 ? "s" : ""}
      </span>
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {((selectedItem as any).updated_by as { id?: number; at?: string }[]).slice().reverse().slice(0, showAllUpdates ? undefined : 4).map((entry, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, background: i === 0 ? "#f0fdf4" : "#f8fafc", border: `1px solid ${i === 0 ? "#bbf7d0" : "#e2e8f0"}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: i === 0 ? "linear-gradient(135deg, #10b981, #059669)" : "linear-gradient(135deg, #94a3b8, #64748b)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
              {String(resolveUserName(entry.id) ?? "?")[0].toUpperCase()}
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{resolveUserName(entry.id)}</span>
            {i === 0 && (<span style={{ fontSize: 10, padding: "1px 8px", borderRadius: 9999, background: "#dcfce7", color: "#15803d", fontWeight: 600, border: "1px solid #bbf7d0" }}>Latest</span>)}
          </div>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 500 }}>{formatDate(entry.at)}</span>
        </div>
      ))}
      {(selectedItem as any).updated_by.length > 4 && (
        <div style={{ textAlign: "center", padding: "6px 0" }}>
          <button
            onClick={() => setShowAllUpdates(v => !v)}
            style={{ fontSize: 11, color: "#3b82f6", fontWeight: 600, background: "none", border: "none", cursor: "pointer" }}
          >
            {showAllUpdates ? "Show less ↑" : `+${(selectedItem as any).updated_by.length - 4} more updates ↓`}
          </button>
        </div>
      )}
    </div>
  </div>
)}
                </div>
              </div>

              <div style={{ padding: "14px 28px", borderTop: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                <Button onClick={handleCloseDialog} style={{ background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", fontWeight: 600, padding: "8px 28px", borderRadius: 10, boxShadow: "0 2px 8px rgba(37,99,235,0.3)" }}>Close Details</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add / Edit Dialog ── */}
 {/* ── Add / Edit Dialog ── */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                {editIndex === null ? "Add Custom ICD Group" : "Edit Custom ICD Group "}
              </DialogTitle>
              </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-0">
            {/* Section 1 */}
   {/* Section 1 */}
            <SectionCard num={1} title="Basic information" subtitle="Name, description, and status of the group">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name <span className="text-red-500 ml-0.5">*</span></Label>
                  <Input id="name" value={Name} onChange={(e) => setName(e.target.value)} placeholder="Must be at least 3 characters" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Input id="description" value={Description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional description" className="mt-1" />
                </div>
                <div className="col-span-2 max-w-xs">
                  <StatusToggle label="Group Status" dotColor="#2563eb" value={isActive} onChange={setIsActive} />
                </div>
              </div>
            </SectionCard>

            {/* Section 2 */}
            <SectionCard num={2} title="ICD configuration" subtitle="At least one ICD code is required">
              <div className="space-y-4">
                <div>
                  <Label className="mb-1.5 block">List of ICD <span className="text-red-500 ml-0.5">*</span></Label>
                  <Listofmedicalcode onChange={handlemedicalcodeChange} description={IcdDescription} defaultValue={medicalcode} clear={clearmedicalcode} />
                </div>
                <div>
                  <Label className="mb-1.5 block">Medical ICD Groups</Label>
                  <ListofMedicalGroup onChange={handlemedicalicdChange} defaultValue={medicalIcdGroups} clear={clearmedicalIcdGroups} />
                </div>
                <div>
                  <Label className="mb-1.5 block">Custom ICD Groups</Label>
                  <ListofCustom onChange={handlecustomicdChange} defaultValue={CustomIcdGroups} clear={clearcustomIcdGroups} />
                </div>
              </div>
            </SectionCard>

            {/* Section 3 */}
         
          </div>

          <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
            <Button variant="outline" onClick={() => { setEditIndex(null); clear(); setIsDialogOpen(false); }}>Cancel</Button>
            <Button onClick={() => { editIndex === null ? addItem() : updateItem(editIndex); }} className="bg-blue-700 hover:bg-blue-800 text-white">
              {editIndex === null ? "Add Group " : "Update Group"} 
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Force / Hard Delete Dialog ── */}
      {forceDialogOpen && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)" }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 32, width: 440, boxShadow: "0 20px 50px rgba(0,0,0,0.3)", border: "1px solid #fecaca" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12 }}>
              <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: 28, color: "#dc2626" }}></i>
              </div>
{forceAction === "hardDelete" && (
  <>
    <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
      Permanent Delete
    </h3>
    <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
      This will <strong style={{ color: "#dc2626" }}>permanently delete</strong> the record.<br />
      This action <strong>cannot be undone</strong>.
    </p>
    <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
      <button
        onClick={() => { setForceDialogOpen(false); setForceId(null); setForceError(""); setForceAction(null); }}
        style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
        Cancel
      </button>
      <button
        onClick={() => forceId !== null && permanentDeleteItem(forceId, true)}
        style={{ flex: 1, background: "#7f1d1d", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
        <i className="fa-solid fa-bolt mr-2"></i>Force Delete Anyway
      </button>
    </div>
  </>
)}
          {(forceAction === "softDelete" || forceAction === "status") && (
  <>
    <h3 style={{ fontSize: 18, fontWeight: 700, color: "#1e293b", margin: 0 }}>
      {forceAction === "status" ? "Status Change Warning" : "Record Is In Use"}
    </h3>
    <div style={{ width: "100%", background: "#fef9c3", border: "1px solid #fde047", borderRadius: 10, padding: "12px 16px", textAlign: "left" }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
        <i className="fa-solid fa-circle-info" style={{ color: "#ca8a04", marginTop: 2, flexShrink: 0 }}></i>
        <p style={{ fontSize: 13, color: "#713f12", margin: 0, lineHeight: 1.6 }}>{forceError}</p>
      </div>
    </div>
    <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
      Do you want to <strong style={{ color: "#dc2626" }}>force {forceAction === "status" ? "update status" : "delete"}</strong> anyway?
    </p>
    <div style={{ display: "flex", gap: 10, width: "100%", marginTop: 4 }}>
      <button
        onClick={() => { setForceDialogOpen(false); setForceId(null); setForceTargetItem(null); setForceError(""); setForceAction(null); }}
        style={{ flex: 1, background: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 600, fontSize: 13 }}>
        Cancel
      </button>
      <button
        onClick={() => {
          if (!forceId) return;
          if (forceAction === "softDelete") forceSoftDelete(forceId);
          else if (forceAction === "status" && forceTargetItem) toggleActiveStatus(forceTargetItem, true);
        }}
        style={{ flex: 1, background: "#dc2626", color: "#fff", border: "none", borderRadius: 9, padding: 12, cursor: "pointer", fontWeight: 700, fontSize: 13 }}>
        <i className="fa-solid fa-bolt" style={{ marginRight: 8 }}></i>
        Force {forceAction === "status" ? "Update" : "Delete"}
      </button>
    </div>
  </>
)}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default CustomIcdGroups;